import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

/**
 * Pre-configured presets for common Gateway UI elements.
 */
const PRESETS = {
  text: { variant: 'text', height: 20, width: 120, sx: { borderRadius: 0.75 } },
  number: { variant: 'text', height: 36, width: 80, sx: { borderRadius: 1 } },
  display: { variant: 'text', height: 42, width: 100, sx: { borderRadius: 1 } },
  caption: { variant: 'text', height: 16, width: 90, sx: { borderRadius: 0.5 } },
  chip: { variant: 'rounded', height: 24, width: 70, sx: { borderRadius: '12px' } },
  badge: { variant: 'rounded', height: 20, width: 60, sx: { borderRadius: '10px' } },
  avatar: { variant: 'circular', height: 40, width: 40 },
  avatarSmall: { variant: 'circular', height: 32, width: 32 },
  card: { variant: 'rounded', height: 120, width: '100%', sx: { borderRadius: '14px' } },
  row: { variant: 'rounded', height: 48, width: '100%', sx: { borderRadius: 1 } },
  switchRow: { variant: 'rounded', height: 44, width: '100%', sx: { borderRadius: 1.5 } },
  table: { isCustom: true },
};

/**
 * Realistic table skeleton placeholder with exact MUI Table cell geometry and padding.
 */
const TableSkeleton = ({ rows = 5, cols = 6, height, animation = 'wave' }) => (
  <TableContainer sx={height ? { minHeight: height } : undefined}>
    <Table size="small">
      <TableHead>
        <TableRow>
          {Array.from({ length: cols }).map((_, idx) => (
            <TableCell key={idx}>
              <Skeleton
                variant="text"
                height={16}
                width={idx === 0 ? '60%' : '75%'}
                animation={animation}
              />
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.from({ length: rows }).map((_, rIdx) => (
          <TableRow key={rIdx}>
            {Array.from({ length: cols }).map((_, cIdx) => (
              <TableCell key={cIdx}>
                <Skeleton
                  variant="text"
                  height={20}
                  width={cIdx === 0 ? '80%' : cIdx === cols - 1 ? '40%' : '60%'}
                  animation={animation}
                />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const DEFAULT_FADE_DURATION = 750; // 0.75s smooth crossfade exit duration matching Visitantes hoje
const MIN_SKELETON_TIME = 250; // 250ms minimum display to ensure animation is perceptible across all cards

/**
 * Reusable LoadingSkeleton component with smooth 0.75s crossfade transitions.
 * Wraps dynamic content that depends on an API request or filter state.
 *
 * @example
 * <LoadingSkeleton loading={loading} preset="number">
 *   <Typography variant="h4">{count}</Typography>
 * </LoadingSkeleton>
 */
const LoadingSkeleton = ({
  loading = false,
  preset = 'text',
  variant,
  width,
  height,
  count = 1,
  animation = 'wave',
  duration = DEFAULT_FADE_DURATION,
  enterDuration = DEFAULT_FADE_DURATION,
  customSkeleton = null,
  sx = {},
  children,
}) => {
  const presetConfig = PRESETS[preset] || PRESETS.text;

  const finalVariant = variant || presetConfig.variant || 'text';
  const finalWidth = width !== undefined ? width : presetConfig.width;
  const finalHeight = height !== undefined ? height : presetConfig.height;
  const finalSx = { ...presetConfig.sx, ...sx };

  // Track phase: 'LOADING' | 'FADING_OUT' | 'IDLE'
  const [phase, setPhase] = useState(() => (loading ? 'LOADING' : 'IDLE'));
  const [prevLoading, setPrevLoading] = useState(loading);
  const loadingStartTimeRef = useRef(loading ? Date.now() : 0);
  const timerRef = useRef(null);

  if (loading !== prevLoading) {
    setPrevLoading(loading);
    if (loading) {
      loadingStartTimeRef.current = Date.now();
      setPhase('LOADING');
    }
  }

  useEffect(() => {
    if (!loading && phase === 'LOADING') {
      const elapsed = Date.now() - loadingStartTimeRef.current;
      const remaining = Math.max(0, MIN_SKELETON_TIME - elapsed);

      timerRef.current = setTimeout(() => {
        setPhase('FADING_OUT');
        timerRef.current = setTimeout(() => {
          setPhase('IDLE');
        }, duration);
      }, remaining);
    } else if (loading) {
      if (timerRef.current) clearTimeout(timerRef.current);
      setPhase('LOADING');
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [loading, phase, duration]);

  const isSkeletonActive = loading || phase === 'LOADING';
  const isFadingOut = !loading && phase === 'FADING_OUT';
  const showSkeleton = isSkeletonActive || isFadingOut;
  const contentOpacity = isSkeletonActive ? 0 : 1;
  const skeletonOpacity = isSkeletonActive ? 1 : 0;

  const contentTransition = isFadingOut ? `opacity ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)` : 'none';
  const skeletonTransition = isFadingOut ? `opacity ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)` : 'none';

  const displayedChildren = children;

  const renderSkeletonContent = () => {
    if (customSkeleton) {
      return customSkeleton;
    }
    if (preset === 'table') {
      const tableRows = count !== undefined && count > 0 ? count : (children ? 3 : 5);
      return <TableSkeleton height={height !== undefined ? height : undefined} animation={animation} rows={tableRows} />;
    }
    if (count > 1) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
          {Array.from({ length: count }).map((_, idx) => (
            <Skeleton
              key={idx}
              variant={finalVariant}
              width={finalWidth}
              height={finalHeight}
              animation={animation}
              sx={finalSx}
            />
          ))}
        </Box>
      );
    }
    return (
      <Skeleton
        variant={finalVariant}
        width={finalWidth}
        height={finalHeight}
        animation={animation}
        sx={finalSx}
      />
    );
  };

  if (!children) {
    if (!showSkeleton) return null;
    return (
      <Box
        sx={{
          opacity: skeletonOpacity,
          transition: skeletonTransition,
          ...(isSkeletonActive && {
            animation: `gwSkeletonFadeIn ${enterDuration}ms ease-out`,
            '@keyframes gwSkeletonFadeIn': {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }),
          width: '100%',
        }}
      >
        {renderSkeletonContent()}
      </Box>
    );
  }

  const skeletonElement = renderSkeletonContent();

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gridTemplateRows: '1fr',
        position: 'relative',
        width: '100%',
      }}
    >
      {/* Content Layer */}
      <Box
        data-testid="loaded-content-layer"
        sx={{
          gridArea: '1 / 1 / 2 / 2',
          opacity: contentOpacity,
          transition: contentTransition,
          pointerEvents: isSkeletonActive ? 'none' : 'auto',
          minWidth: 0,
        }}
      >
        {displayedChildren}
      </Box>

      {/* Skeleton Layer */}
      {showSkeleton && (
        <Box
          aria-hidden={!isSkeletonActive}
          sx={{
            gridArea: '1 / 1 / 2 / 2',
            opacity: skeletonOpacity,
            transition: skeletonTransition,
            pointerEvents: 'none',
            width: '100%',
            minWidth: 0,
            ...(isSkeletonActive && {
              animation: `gwSkeletonFadeIn ${enterDuration}ms ease-out`,
              '@keyframes gwSkeletonFadeIn': {
                from: { opacity: 0 },
                to: { opacity: 1 },
              },
            }),
          }}
        >
          {skeletonElement}
        </Box>
      )}
    </Box>
  );
};

LoadingSkeleton.propTypes = {
  loading: PropTypes.bool,
  preset: PropTypes.oneOf([
    'text',
    'number',
    'display',
    'caption',
    'chip',
    'badge',
    'avatar',
    'avatarSmall',
    'card',
    'row',
    'switchRow',
    'table',
  ]),
  variant: PropTypes.oneOf(['text', 'rectangular', 'rounded', 'circular']),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  count: PropTypes.number,
  animation: PropTypes.oneOf(['wave', 'pulse', false]),
  duration: PropTypes.number,
  enterDuration: PropTypes.number,
  customSkeleton: PropTypes.node,
  sx: PropTypes.object,
  children: PropTypes.node,
};

export default LoadingSkeleton;
